import { useEffect, useState } from 'react';
import { useLatestCallbackRef } from '../Hooks/useLatestCallbackRef';

type Listener = () => void;

export type ReadonlyStore<T> = {
    get: () => T;
    subscribe: (listener: Listener) => () => void;
};

export type Store<T> = ReadonlyStore<T> & {
    set: (next: T) => void;
};

export function createStore<T>(initialValue: T): Store<T> {
    let value = initialValue;
    const listeners = new Set<Listener>();

    return {
        get: () => value,
        set: (next: T) => {
            value = next;
            listeners.forEach((l) => l());
        },
        subscribe: (listener: Listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}

export function useFrozenStoreState<T>(store: ReadonlyStore<T>, isFrozen: () => boolean, ms: number = 1000): T {
    const [state, setState] = useState<T>(store.get());

    const isFrozenRef = useLatestCallbackRef(isFrozen);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;

        interval = setInterval(() => {
            if (!isFrozenRef.current()) {
                setState(store.get());
            }
        }, ms);

        return () => clearInterval(interval);
    }, [store, ms, isFrozenRef]);

    return state;
}
